import {
  Component,
  ContentChild,
  inject,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { PackingBoxCreateLabelType } from '../../types/create-label-dialog';
import { PackingBoxCreateLabelService } from './create-label.service';
import { PackingBoxCreateLabelFormDirective } from './create-label.directive';
import { Subscription } from 'rxjs';
import { PrinterDepartment } from 'src/app/api/elite/types/printer-department';

@Component({
  selector: 'app-vw-pb-create-label',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    MatSlideToggleModule,
  ],
  templateUrl: './create-label.component.html',
  styleUrls: ['./create-label.component.sass'],
})
export class PackingBoxCreateLabelComponent implements OnInit {
  readonly data: PackingBoxCreateLabelType['data'] = inject(MAT_DIALOG_DATA);
  protected readonly dialogRef = inject(MatDialogRef);
  protected readonly service = inject(PackingBoxCreateLabelService);
  private sub!: Subscription;

  get maxQuantity(): number {
    return this.service.maxQuantity;
  }
  lots = [...this.data.lotNumbers];

  isStockType = this.data.isToStock;
  showLotNumber = this.data.lotNumbers.length > 0;

  salesOrderTitle = this.data.salesOrder;

  protected get form(): FormGroup {
    return this.service.form;
  }

  printers: PrinterDepartment[] = [];

  @ContentChild(PackingBoxCreateLabelFormDirective, { read: TemplateRef })
  templateForm: TemplateRef<any> | undefined = undefined;

  private assignPrinters(): void {
    this.printers = [
      {
        id: 'auto-selection',
        printerName: 'Impresión Local',
        department: this.data.department,
      },
      ...(this.data.printers ?? []),
    ];
    // Pre-select the synthetic local entry so the form is valid by
    // default — the dropdown remains open and the operator can switch
    // to a station if one is available. Covers the common case where
    // the department has no stations and "Impresión Local" was always
    // the intended pick anyway.
    this.form
      .get('printer')
      ?.setValue(
        this.generatePrinterName('Impresión Local', this.data.department)
      );
  }

  generatePrinterName(printerName: string, department: string): string {
    // Null-safe: some flows (e.g. compounding) open the dialog without a
    // `department`, which left the synthetic "Impresión Local" entry with
    // `department === undefined` and crashed on `.toUpperCase()`. Drop the
    // " - <dep>" suffix when there's no department instead of throwing.
    const name = (printerName ?? '').toUpperCase();
    const dep = (department ?? '').toUpperCase();
    return dep ? `${name} - ${dep}` : name;
  }

  ngOnInit(): void {
    this.assignPrinters();
    this.service.setMaxQuantity(this.data.maxQuantity);
    const addSalesOrderControl = this.form.get('addSalesOrder');
    if (this.data?.department?.toLocaleLowerCase() === 'plastics') {
      addSalesOrderControl?.setValue(false);
    }

    if (this.data.selectSalesOrder === false) {
      this.salesOrderTitle = undefined;
      addSalesOrderControl.setValue(false);
    }

    if (addSalesOrderControl.value) {
      if (this.data.maxQuantitySO < this.data.maxQuantity) {
        this.service.setMaxQuantity(this.data.maxQuantitySO);
      }
    }
    if (this.data.lotNumbers.length > 0) {
      this.form.get('lotNumber').setValidators(Validators.required);
    }
    if (this.data.lotNumbers.length === 1) {
      this.form.get('lotNumber').setValue(this.data.lotNumbers[0]);
      this.form.get('lotNumber').disable();
    }
    this.sub = addSalesOrderControl.valueChanges.subscribe((value) => {
      if (value) {
        if (this.data.maxQuantitySO < this.data.maxQuantity) {
          this.service.setMaxQuantity(this.data.maxQuantitySO);
        }
      } else {
        this.service.setMaxQuantity(this.data.maxQuantity);
      }
    });
  }
  onSave(): void {
    const res: PackingBoxCreateLabelType['response'] & {
      printerWithDep?: string;
    } = this.service.onSave(this.data);

    const printerWithDepartment: string = res.printerWithDep;

    if (printerWithDepartment) {
      const searchPrinter = this.data.printers?.find((print) => {
        return (
          this.generatePrinterName(print.printerName, print.department) ===
          printerWithDepartment
        );
      });

      if (searchPrinter) {
        res.printerName = searchPrinter.printerName;
        res.printerDepartment = searchPrinter.department;
      }
    }

    if (res) {
      this.dialogRef.close(res);
    }
  }
}
